import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectRouteType,
    selectStartPoint,
    selectEndPoint,
    selectViaPoints,
    selectRoutePath,
    selectRouteDistance,
    selectRouteTime,
    setComputeRoute,
    generateRouteSegments,
    setWizardStepCompleted,
    checkRouteStepCompleted,
    setIsMinimized,
    addViaPoint,
    setEndPoint,
    clearViaPoints
} from '../../../features/tripcenter/googleRouteMakerSlice';
import PropTypes from 'prop-types';

// MUI components
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import IconButton from '@mui/material/IconButton';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';

// Icons
import RouteIcon from '@mui/icons-material/Route';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import StraightenIcon from '@mui/icons-material/Straighten';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ReplayIcon from '@mui/icons-material/Replay';
import EditRoadIcon from '@mui/icons-material/EditRoad';

const RouteBuilder = ({ onConfirm, isRouteCalculated }) => {
    const dispatch = useDispatch();

    // Get route data
    const routeType = useSelector(selectRouteType);
    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const routePath = useSelector(selectRoutePath);
    const routeDistance = useSelector(selectRouteDistance);
    const routeTime = useSelector(selectRouteTime);

    // Local state
    const [isCalculating, setIsCalculating] = useState(false);
    const [error, setError] = useState(null);

    // Format route distance and time
    const formattedDistance = (routeDistance / 1000).toFixed(1);
    const formattedTime = formatTime(routeTime);

    function formatTime(seconds) {
        if (!seconds) return '0 min';

        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);

        if (hours > 0) {
            return `${hours} hr ${minutes} min`;
        } else {
            return `${minutes} min`;
        }
    }

    // Check if we can calculate route
    const canCalculateRoute = () => {
        return startPoint && endPoint;
    };

    // Calculate route
    const handleCalculateRoute = () => {
        setIsCalculating(true);
        setError(null);
        //console.log("RouteBuilder: Calculating route...", routeType);
        // For round trips, modify the route calculation
        if (routeType === 'roundTrip' || routeType === 'roundTripWithStops') {
            // Clear existing via points first
            if (routeType === 'roundTrip') {
                console.log("RouteBuilder: Clearing via points for round trip", routeType);
                dispatch(clearViaPoints());
            }

            // Check if endPoint exists and is not the same as the startPoint
            if (endPoint && (endPoint.lat !== startPoint.lat || endPoint.lng !== startPoint.lng)) {
                // Make sure we're sending the data in the correct format
                dispatch(addViaPoint({
                    lat: endPoint.lat,
                    lng: endPoint.lng,
                    address: endPoint.address,
                    locationName: endPoint.locationName,
                    locationType: endPoint.locationType,
                    zoneCoordinates: endPoint.zoneCoordinates,
                    triggerRadius: endPoint.triggerRadius,
                    maxDetentionTime: endPoint.maxDetentionTime
                }));
            }

            // Set the end point to be the same as start point for round trip
            dispatch(setEndPoint({
                ...startPoint,
                locationName: `${startPoint.locationName} (Return)`
            }));
        }

        // Trigger route calculation
        dispatch(setComputeRoute(true));

        // This would normally be handled by the GoogleRouteMaker component
        // which would update the route data in Redux
        // For now, we'll simulate completion after a short delay
        setTimeout(() => {
            setIsCalculating(false);

            // If the route was calculated successfully, update step completion
            if (routePath && routePath.length > 0) {
                dispatch(setWizardStepCompleted({ step: 'route', completed: true }));

                // Generate route segments
                dispatch(generateRouteSegments());
            } else {
                setError('Route calculation failed. Please try again.');
            }
        }, 2000);
    };

    // Recalculate route
    const handleRecalculateRoute = () => {
        handleCalculateRoute();
    };

    // Update completion status when route path changes
    useEffect(() => {
        dispatch(checkRouteStepCompleted());
    }, [routePath, dispatch]);

    // When component mounts, scroll the map view to focus on locations
    useEffect(() => {
        // In a real implementation, we would add code here to adjust the map view
        // to show all our route points (start, via, end)
    }, []);

    // Update the continue button click handler
    const handleContinue = () => {
        //console.log("RouteBuilder: Continuing to next step...");
        if (typeof onConfirm === 'function') {
            onConfirm();
        }
    };

    const handleMinimize = () => {
        //console.log("RouteBuilder: Minimizing...");
        dispatch(setIsMinimized(true));
    };

    const renderRouteDetails = () => {
        if (!isRouteCalculated) return null;

        return (
            <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" mb={2}>
                    <StraightenIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">
                        Distance: <strong>{formattedDistance} km</strong>
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" mb={2}>
                    <AccessTimeIcon sx={{ mr: 1, color: 'primary.main' }} />
                    <Typography variant="body2">
                        Estimated time: <strong>{formattedTime}</strong>
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        variant="outlined"
                        startIcon={<ReplayIcon />}
                        onClick={handleRecalculateRoute}
                        size="small"
                        fullWidth
                    >
                        Recalculate
                    </Button>
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: 2 }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                <Typography variant="h6">
                    Route Builder
                </Typography>
                <IconButton
                    onClick={handleMinimize}
                    size="small"
                >
                    <CloseFullscreenIcon />
                </IconButton>
            </Box>

            <Typography variant="subtitle1" gutterBottom>
                Route Summary
            </Typography>

            <Box sx={{ mb: 2 }}>
                <Box display="flex" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="body2" fontWeight="medium" color="primary.main">
                        {startPoint?.locationName || 'Start'}
                    </Typography>

                    {viaPoints.length > 0 && (
                        <>
                            <Typography variant="caption" sx={{ mx: 1 }}>via</Typography>
                            <Typography variant="body2" fontWeight="medium" color="info.main">
                                {viaPoints.length} stop{viaPoints.length > 1 ? 's' : ''}
                            </Typography>
                        </>
                    )}

                    <Typography variant="caption" sx={{ mx: 1 }}>to</Typography>

                    <Typography variant="body2" fontWeight="medium" color="secondary.main">
                        {endPoint?.locationName || 'End'}
                    </Typography>
                </Box>

                <Chip
                    label={routeType === 'roundTrip' || routeType === 'roundTripWithStops' ? "Round Trip" : "One Way"}
                    color="primary"
                    variant="outlined"
                    size="small"
                />
            </Box>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!isRouteCalculated ? (
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<RouteIcon />}
                    onClick={handleCalculateRoute}
                    disabled={!canCalculateRoute() || isCalculating}
                    fullWidth
                >
                    {isCalculating ? 'Calculating...' : 'Calculate Route'}
                </Button>
            ) : (
                renderRouteDetails()
            )}

            {isCalculating && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <CircularProgress size={24} />
                </Box>
            )}

            {isRouteCalculated && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleContinue}
                    fullWidth
                    sx={{ mt: 2 }}
                >
                    Continue to Next Step
                </Button>
            )}
        </Box>
    );
};

RouteBuilder.propTypes = {
    onConfirm: PropTypes.func.isRequired,
    isRouteCalculated: PropTypes.bool.isRequired
};

export default RouteBuilder; 